import { Box, Switch, Typography, Divider, useTheme, Tooltip } from '@mui/material';
import { NotificationsOutlined } from '@mui/icons-material';
import SettingCard from './SettingCard';
import { useTranslation } from '@hooks/useTranslation';
import { NotificationPreferences, NotificationChannels } from '@models/user.model';

interface NotificationsSectionProps {
  notificationPreferences: NotificationPreferences | undefined;
  onPreferenceChange: (
    category: keyof NotificationPreferences,
    channel: keyof NotificationChannels,
    value: boolean
  ) => void;
  isSlackConnected?: boolean;
}

// Define which channels are available for each category and their defaults
type ChannelConfig = {
  available: boolean;
  defaultValue: boolean;
  disabled?: boolean;
};

type CategoryConfig = {
  mail?: ChannelConfig;
  badge?: ChannelConfig;
  slack?: ChannelConfig;
};

// Badge pairs that should stay in sync (they control the same sidebar badge)
const BADGE_PAIRS: Record<string, keyof NotificationPreferences> = {
  discrepancies: 'wolfPendings',
  wolfPendings: 'discrepancies',
  assignments: 'executionReminders',
  executionReminders: 'assignments',
};

const NOTIFICATION_CONFIG: Record<keyof NotificationPreferences, CategoryConfig> = {
  marketing: {
    mail: { available: true, defaultValue: true },
  },
  assignments: {
    mail: { available: true, defaultValue: true },
    badge: { available: true, defaultValue: true },
    slack: { available: true, defaultValue: false },
  },
  executionReminders: {
    mail: { available: true, defaultValue: true },
    badge: { available: true, defaultValue: true },
    slack: { available: true, defaultValue: false },
  },
  organizationChanges: {
    mail: { available: true, defaultValue: true },
  },
  discrepancies: {
    mail: { available: true, defaultValue: false },
    badge: { available: true, defaultValue: true },
    slack: { available: true, defaultValue: false },
  },
  wolfPendings: {
    mail: { available: true, defaultValue: false },
    badge: { available: true, defaultValue: true },
    slack: { available: true, defaultValue: false },
  },
  calendarTrigger: {
    mail: { available: true, defaultValue: false },
    badge: { available: true, defaultValue: false, disabled: true },
    slack: { available: true, defaultValue: false },
  },
};

interface NotificationRowProps {
  category: keyof NotificationPreferences;
  title: string;
  description: string;
  preferences: NotificationPreferences | undefined;
  onPreferenceChange: (
    category: keyof NotificationPreferences,
    channel: keyof NotificationChannels,
    value: boolean
  ) => void;
  localeSettings: Record<string, string>;
  badgePair?: keyof NotificationPreferences;
  isSlackConnected?: boolean;
}

const NotificationRow: React.FC<NotificationRowProps> = ({
  category,
  title,
  description,
  preferences,
  onPreferenceChange,
  localeSettings,
  badgePair,
  isSlackConnected,
}) => {
  const config = NOTIFICATION_CONFIG[category];

  const getChannelValue = (channel: keyof NotificationChannels): boolean => {
    const channelConfig = config[channel];
    if (!channelConfig?.available) return false;
    // Slack requires connection to be enabled
    if (channel === 'slack' && !isSlackConnected) return false;
    return preferences?.[category]?.[channel] ?? channelConfig.defaultValue;
  };

  const isChannelDisabled = (channel: keyof NotificationChannels): boolean => {
    if (channel === 'slack') {
      return !isSlackConnected;
    }
    return config[channel]?.disabled ?? false;
  };

  const availableChannels = (['mail', 'badge', 'slack'] as const).filter(
    (channel) => config[channel]?.available
  );

  return (
    <Box className="py-4">
      <Box className="mb-3">
        <Typography variant="h4" className="font-semibold">
          {title}
        </Typography>
        <Typography variant="body14" className="text-gray-600">
          {description}
        </Typography>
      </Box>
      <Box className="flex flex-wrap gap-4 ml-2">
        {availableChannels.map((channel) => {
          const disabled = isChannelDisabled(channel);
          const isBadgeWithPair = channel === 'badge' && badgePair;
          const switchElement = (
            <Switch
              size="small"
              checked={getChannelValue(channel)}
              onChange={(e) => onPreferenceChange(category, channel, e.target.checked)}
              color="primary"
              disabled={disabled}
            />
          );

          const getTooltip = (): string | null => {
            if (channel === 'slack' && !isSlackConnected) {
              return localeSettings['slackNotConnected'] || 'Admin must connect Slack first';
            }
            if (disabled && !isBadgeWithPair) return localeSettings['underImplementation'];
            if (isBadgeWithPair) return localeSettings['sharedBadgeTooltip'];
            return null;
          };

          const tooltip = getTooltip();

          return (
            <Box key={channel} className="flex items-center gap-2 min-w-[120px]">
              <Typography variant="body2" className="text-gray-700 min-w-[50px]">
                {localeSettings[`notificationChannel${channel.charAt(0).toUpperCase() + channel.slice(1)}`]}:
              </Typography>
              {tooltip ? (
                <Tooltip title={tooltip} arrow>
                  <span>{switchElement}</span>
                </Tooltip>
              ) : (
                switchElement
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const NotificationsSection: React.FC<NotificationsSectionProps> = ({
  notificationPreferences,
  onPreferenceChange,
  isSlackConnected,
}) => {
  const theme = useTheme();
  const localeSettings = useTranslation('settings');


  const categories: Array<{
    key: keyof NotificationPreferences;
    titleKey: string;
    descriptionKey: string;
  }> = [
    {
      key: 'marketing',
      titleKey: 'marketingNotifications',
      descriptionKey: 'marketingNotificationsDescription',
    },
    {
      key: 'assignments',
      titleKey: 'assignmentNotificationsTitle',
      descriptionKey: 'assignmentNotificationsDesc',
    },
    {
      key: 'executionReminders',
      titleKey: 'executionReminders',
      descriptionKey: 'executionRemindersDescription',
    },
    {
      key: 'organizationChanges',
      titleKey: 'organizationChanges',
      descriptionKey: 'organizationChangesDescription',
    },
    {
      key: 'discrepancies',
      titleKey: 'discrepancies',
      descriptionKey: 'discrepanciesDescription',
    },
    {
      key: 'wolfPendings',
      titleKey: 'wolfPendings',
      descriptionKey: 'wolfPendingsDescription',
    },
    {
      key: 'calendarTrigger',
      titleKey: 'calendarTrigger',
      descriptionKey: 'calendarTriggerDescription',
    },
  ];

  return (
    <SettingCard
      title={localeSettings['notificationPreferences']}
      icon={<NotificationsOutlined />}
      iconColor={theme.palette.frozenGrayShades7.main}>
      {categories.map((category, index) => (
        <Box key={category.key}>
          <NotificationRow
            category={category.key}
            title={localeSettings[category.titleKey]}
            description={localeSettings[category.descriptionKey]}
            preferences={notificationPreferences}
            onPreferenceChange={onPreferenceChange}
            localeSettings={localeSettings}
            badgePair={BADGE_PAIRS[category.key]}
            isSlackConnected={isSlackConnected}
          />
          {index < categories.length - 1 && <Divider />}
        </Box>
      ))}
    </SettingCard>
  );
};

export default NotificationsSection;
